// Service-worker registration WITH a user-gated update flow.
//
// The SW claims clients on activate and purges old caches, so an unconditional
// takeover could hand a live tab a shell whose hashed assets a new deploy just
// removed -> 404s until a manual reload. Instead a new worker WAITS; we surface a
// quiet "Refresh" toast; only the user's click sends SKIP_WAITING and reloads on
// controllerchange. First install activates silently (nothing controls the page yet).

export function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator)) return;

  void navigator.serviceWorker
    .register("/sw.js")
    .then((reg) => {
      // An update that finished installing during a prior visit is already waiting.
      if (reg.waiting && navigator.serviceWorker.controller) showUpdateToast(reg.waiting);
      reg.addEventListener("updatefound", () => {
        const next = reg.installing;
        if (!next) return;
        next.addEventListener("statechange", () => {
          // "installed" + an existing controller == a genuine update (not first install).
          if (next.state === "installed" && navigator.serviceWorker.controller) showUpdateToast(next);
        });
      });
    })
    .catch(() => {
      /* registration failure is non-fatal - the app still runs online */
    });

  // Reload ONLY after the user explicitly clicked Refresh (the `refreshing` flag).
  // NEVER on an incidental controllerchange - e.g. a waiting worker activating when
  // another tab closes, or a first install's clients.claim() - which would reload the
  // page mid-interaction (open a source -> reload -> back to verses) and feel glitchy.
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing) location.reload();
  });
}

// Set true only by the user's Refresh click, so controllerchange reloads ONLY then.
let refreshing = false;
let toastUp = false;
function showUpdateToast(worker: ServiceWorker): void {
  if (toastUp) return;
  toastUp = true;

  const toast = document.createElement("div");
  toast.className = "desk-toast sw-toast";
  toast.setAttribute("role", "status");

  const msg = document.createElement("span");
  msg.className = "desk-toast-msg";
  msg.textContent = "A new version of TorahTrail is ready.";

  const refresh = document.createElement("button");
  refresh.type = "button";
  refresh.className = "desk-toast-action";
  refresh.textContent = "Refresh";
  refresh.addEventListener("click", () => {
    refreshing = true;
    worker.postMessage({ type: "SKIP_WAITING" });
  });

  const later = document.createElement("button");
  later.type = "button";
  later.className = "desk-toast-action sw-toast-dismiss";
  later.textContent = "Later";
  later.setAttribute("aria-label", "Dismiss the update notice");
  later.addEventListener("click", () => {
    toast.remove();
    toastUp = false;
  });

  toast.append(msg, refresh, later);
  document.body.append(toast);
}
